import type { InteractionMode, RouteId } from './types'

function routeKey(route: RouteId | undefined): string | null {
  return route?.id && route.version ? `${route.id}\0${route.version}` : null
}

export function isSameRouteKey(a: RouteId | undefined, b: RouteId | undefined): boolean {
  return routeKey(a) === routeKey(b)
}

export type RouteChangeReset = 'none' | 'refit' | 'invalidate' | 'clear'

/**
 * What the planner state should do when the selected route changes.
 * - 'refit': same loaded route re-selected — just re-frame the camera.
 * - 'invalidate': another saved route (or version) incoming — keep the path
 *   visible while it loads, drop the loaded key. Same-id version changes
 *   invalidate even when the next version is not listed yet (just-saved
 *   versions arrive before the route list refetches).
 * - 'clear': leaving saved routes entirely.
 * - 'none': create-mode draft churn — never touch the drawn path.
 */
export function resolveRouteChangeReset(
  prev: RouteId | undefined,
  next: RouteId | undefined,
  isListed: (route: RouteId) => boolean
): RouteChangeReset {
  if (routeKey(prev) === routeKey(next)) return routeKey(next) !== null ? 'refit' : 'none'
  if (next === undefined) return 'clear'
  if (prev?.id === next.id) return 'invalidate'
  if (isListed(next)) return 'invalidate'
  if (prev !== undefined && isListed(prev)) return 'clear'
  return 'none'
}

export type RouteSelectionSync =
  | { action: 'skip' }
  | { action: 'clear'; createMode: boolean }
  | { action: 'navigate'; route: RouteId }

export function resolveRouteSelectionSync(options: {
  route: RouteId | undefined
  interactionMode: InteractionMode
  isListed: boolean
  forceSync?: boolean
}): RouteSelectionSync {
  const { route, interactionMode, isListed, forceSync } = options
  if (!route) {
    return { action: 'clear', createMode: interactionMode === 'create' }
  }
  if (forceSync || isListed) {
    return { action: 'navigate', route }
  }
  return { action: 'skip' }
}

export function initialInteractionMode(hasInitialRoute: boolean, searchCreateMode: boolean): InteractionMode {
  if (searchCreateMode) return 'create'
  return hasInitialRoute ? 'edit' : 'create'
}

export function resolveInitialRouteForArea(options: {
  searchCreateMode: boolean
  defaultRoute: RouteId | undefined
}): RouteId | undefined {
  if (options.searchCreateMode) return undefined
  return options.defaultRoute
}

export function resolvePlannerBootstrap(options: {
  searchCreateMode: boolean
  deepLinkRoute: RouteId | undefined
  defaultRoute: RouteId | undefined
}): { initialRoute: RouteId | undefined; interactionMode: InteractionMode } {
  if (options.searchCreateMode) {
    return { initialRoute: undefined, interactionMode: 'create' }
  }
  const initialRoute = options.deepLinkRoute ?? options.defaultRoute
  return {
    initialRoute,
    interactionMode: initialRoute ? 'edit' : 'create',
  }
}

export type RouteSelectionChangeMeta = {
  mode?: InteractionMode
  returnRoute?: RouteId
  ignoreBlocker?: boolean
}

export type CreateModeAreaSearch = {
  mode?: 'create'
  returnId?: string
  returnVersion?: string
}

export function createModeAreaSearch(meta: RouteSelectionChangeMeta | undefined): CreateModeAreaSearch {
  if (meta?.mode !== 'create') return {}
  return {
    mode: 'create',
    ...(meta.returnRoute ? { returnId: meta.returnRoute.id, returnVersion: meta.returnRoute.version } : {}),
  }
}

export function returnRouteFromSearch(search: { returnId?: string; returnVersion?: string }): RouteId | undefined {
  if (!search.returnId || !search.returnVersion) return undefined
  return { id: search.returnId, version: search.returnVersion }
}

export type EnterCreateModePlan = {
  interactionMode: 'create'
  clearSelectedRoute: true
  ignoreBlocker: true
  search: CreateModeAreaSearch
}

export function planEnterCreateModeAfterDiscard(previousRoute: RouteId | undefined): EnterCreateModePlan {
  return {
    interactionMode: 'create',
    clearSelectedRoute: true,
    ignoreBlocker: true,
    search: createModeAreaSearch({ mode: 'create', returnRoute: previousRoute }),
  }
}
